import type { IMockStore } from '@graphql-tools/mock';
import type { Education } from '@seek/ca-myprofile-utils';

import { mocks } from 'src/modules/api-client/mocks/spec/';
import type * as Schema from 'src/types/graphql';

import { avatarStatusStore } from '../spec/ProfileAvatarStatus';
import mockResolvers from '../spec/mockResolvers';

import { mockOverrides, MutationResult } from './mockOverrides';
import {
  confirmedRoles,
  unconfirmedRoles,
  confirmedQualifications,
  licences,
  unconfirmedQualifications,
  errorResumeId,
  languageProficiencies,
  salaryPreferences,
  rightsToWork,
  uploadedOKResume,
  preferredLocations2 as preferredLocations2Data,
  updatedCurrentLocation2,
  profileInsightsWithActivities,
  verificationsComplete,
  verificationsDigitalIdNudge,
} from './test-data';
import {
  attachToViewerWithId,
  getStateTesters,
  handleQueryError,
  mapIdToTypeName,
  mergeItems,
  mockError,
} from './utils';
const { returnEmptyState, returnState, returnErrorState } = getStateTesters();

const deleteRole = (
  _: unknown,
  { input }: { input: Schema.DeleteRoleInput },
) => {
  if (input.id === 'acb3ec9f-b227-5165-94b6-aa6612721f67') {
    mockError('deleteRole');
  }

  const updatedRoles = confirmedRoles.filter((role) => role.id !== input.id);

  return attachToViewerWithId({ confirmedRoles: updatedRoles });
};

const updateRole = (
  _: unknown,
  { input }: { input: Omit<Schema.UpdateRoleInput, 'function' | 'industry'> },
) => {
  if (input.title.text === 'Engineer') {
    mockError('updateRole');
  }

  if (returnEmptyState('roles')) {
    return attachToViewerWithId({ confirmedRoles: [input] });
  }

  const updatedRoles = mergeItems(confirmedRoles, input as any);

  return attachToViewerWithId({ confirmedRoles: updatedRoles });
};

const confirmRole = (
  _: unknown,
  { input }: { input: Schema.ConfirmRoleInput },
) => {
  const updatedConfirmedRoles = mergeItems(confirmedRoles, input as any);
  const updatedUnconfirmedRoles = unconfirmedRoles.filter(
    (role) => role.id !== input.id,
  );

  return attachToViewerWithId({
    confirmedRoles: updatedConfirmedRoles,
    unconfirmedRoles: updatedUnconfirmedRoles,
  });
};

const rejectUnconfirmedData = (
  _: unknown,
  { input }: { input: Schema.RejectUnconfirmedDataInput },
) => {
  if (input.dataType === 'CareerHistory') {
    const updatedRoles = unconfirmedRoles.filter(
      (role) => role.id !== input.id,
    );

    return attachToViewerWithId({ unconfirmedRoles: updatedRoles });
  }

  if (input.dataType === 'Education') {
    const updatedQualifications = unconfirmedQualifications.filter(
      (qualification) => qualification.id !== input.id,
    );

    return attachToViewerWithId({
      unconfirmedQualifications: updatedQualifications,
    });
  }
};

const deleteQualification = (
  _: unknown,
  { input }: { input: Schema.DeleteQualificationInput },
) => {
  if (input.id === 'acb3ec9f-b227-5165-94b6-aa6612721f67') {
    mockError('deleteQualification');
  }

  const updatedQualifications = confirmedQualifications.filter(
    (qualification) => qualification.id !== input.id,
  );

  return attachToViewerWithId({
    confirmedQualifications: updatedQualifications,
  });
};

const updateQualification = (
  _: unknown,
  { input }: { input: Schema.UpdateQualificationInput },
) => {
  if (input.institute.text === 'RMIT') {
    mockError('updateQualification');
  }

  if (returnEmptyState('qualifications')) {
    return attachToViewerWithId({ confirmedQualifications: [input] });
  }

  const updatedQualifications = mergeItems(
    confirmedQualifications,
    input as any,
  );

  return attachToViewerWithId({
    confirmedQualifications: updatedQualifications,
  });
};

const updateVerifiedQualification = (
  _: unknown,
  { input }: { input: Schema.UpdateVerifiedQualificationInput },
) => {
  const verifiedQualificationToUpdate = confirmedQualifications.find(
    (qualifications) => qualifications.id === input.id,
  );

  const updatedVerifiedQualification = {
    ...verifiedQualificationToUpdate,
    highlights: input.highlights,
  };

  const updatedVerifiedQualifications = mergeItems(
    confirmedQualifications,
    updatedVerifiedQualification as any,
  );

  return attachToViewerWithId({
    confirmedQualifications: updatedVerifiedQualifications,
  });
};

const confirmQualification = (
  _: unknown,
  { input }: { input: Schema.ConfirmQualificationInput },
) => {
  const updatedConfirmedQualifications = mergeItems(
    confirmedQualifications,
    input as any,
  );
  const updatedUnconfirmedQualifications = unconfirmedQualifications.filter(
    (qualification) => qualification.id !== input.id,
  );

  return attachToViewerWithId({
    confirmedQualifications: updatedConfirmedQualifications,
    unconfirmedQualifications: updatedUnconfirmedQualifications,
  });
};

const updateSkills = (
  _: unknown,
  { input }: { input: Schema.UpdateSkillsInput },
) => {
  if (
    input.skills.filter((skill) => skill.keyword.text === 'Error').length > 0
  ) {
    mockError('updateSkills');
  }

  return attachToViewerWithId(input);
};

const updatePersonalDetails = (
  _: unknown,
  { input }: { input: Schema.UpdatePersonalDetailsInput },
) => {
  if (input.firstName === 'Error' && input.lastName === 'Error') {
    mockError('updatePersonalDetails');
  }

  const personalDetails = {
    ...input,
    defaultCountryCallingCode: { id: '1' },
  };

  return { ...attachToViewerWithId({ personalDetails }) };
};

const updateCurrentLocation2 = (
  _: unknown,
  { input }: { input: Schema.UpdateCurrentLocation2Input },
) => {
  const { description, shortDescription, countryCode, kind } =
    updatedCurrentLocation2;
  return {
    ...attachToViewerWithId({
      currentLocation2: {
        id: input.currentLocation.id,
        description,
        shortDescription,
        countryCode,
        kind,
      },
      salaryPreferences2: salaryPreferences,
      rightsToWork,
      preferredLocations2: [
        ...(!returnEmptyState('preferredLocations')
          ? preferredLocations2Data
          : []),
        {
          id: input.currentLocation.id,
          shortDescription,
          description,
          countryCode,
          kind,
        },
      ],
    }),
  };
};

const updateCareerObjectives = (
  _: unknown,
  { input }: { input: Schema.UpdateCareerObjectivesInput },
) => {
  const { personalStatement } = input;
  if (personalStatement === 'Error') {
    mockError('updateCareerObjectives');
  }

  return attachToViewerWithId({ careerObjectives: { personalStatement } });
};

const updateLicence = (_: unknown, { input }: { input: any }) => {
  if (input.name.text === 'Error') {
    mockError('updateLicence');
  }

  if (returnEmptyState('licences')) {
    return attachToViewerWithId({ licences: [input] });
  }

  const updatedLicences = mergeItems(licences, input);

  return attachToViewerWithId({ licences: updatedLicences });
};

const deleteLicence = (
  _: unknown,
  { input }: { input: Schema.DeleteLicenceInput },
) => {
  const updatedLicences = licences.filter((licence) => licence.id !== input.id);

  return attachToViewerWithId({ licences: updatedLicences });
};

const updateLanguageProficiency = (_: unknown, { input }: { input: any }) => {
  if (returnState('languages:mutation', 'error')) {
    mockError('updateLanguageProficiency');
  }

  if (returnEmptyState('languages')) {
    return { viewer: { languageProficiencies: [input] } };
  }

  const updatedLanguageProficiencies = mergeItems(languageProficiencies, input);

  return { viewer: { languageProficiencies: updatedLanguageProficiencies } };
};

const deleteLanguageProficiency = (
  _: unknown,
  { input }: { input: Schema.DeleteLanguageProficiencyInput },
) => {
  const updatedLanguageProficiencies = languageProficiencies.filter(
    (language) => language.id !== input.id,
  );

  return { viewer: { languageProficiencies: updatedLanguageProficiencies } };
};

const updateProfileVisibility = (
  _: unknown,
  { input }: { input: Schema.UpdateProfileVisibilityInput },
) => {
  if (input.level === 'Hidden') {
    mockError('updateProfileVisibility');
  }

  return attachToViewerWithId({ profileVisibility: input });
};

const updateProfileVisibility2 = (
  _: unknown,
  { input }: { input: Schema.UpdateProfileVisibility2Input },
) => {
  if (input.id === 'Hidden') {
    mockError('updateProfileVisibility2');
  }
  return attachToViewerWithId({
    profileVisibility2: { ...input, ...mapIdToTypeName[input.id] },
  });
};

const deleteProfileAvatar = () => {
  const shouldFail = returnErrorState('deleteProfileAvatar');

  const DeleteProfileAvatarError = {
    message: 'error, try again.',
  };

  if (shouldFail) {
    return {
      __typename: 'DeleteProfileAvatarFailure',
      error: DeleteProfileAvatarError,
    };
  }

  return {
    __typename: 'DeleteProfileAvatarSuccess',
    error: DeleteProfileAvatarError,
  };
};

const createProfileAvatar = () => {
  const shouldFail = returnErrorState('createProfileAvatar');

  if (shouldFail) {
    return {
      __typename: 'CreateProfileAvatarFailure',
      error: {
        __typename: 'CreateProfileAvatarError',
        message: 'Failed to create avatar upload URL. Please try again.',
      },
    };
  }

  const avatarId = `test-avatar-${Date.now()}`;
  const uploadUrl = `https://mock-s3-upload.example.com/upload/${avatarId}`;

  return {
    __typename: 'CreateProfileAvatarSuccess',
    avatarId,
    uploadUrl,
  };
};
const updateAvailability = (
  _: unknown,
  { input }: { input: Schema.UpdateAvailabilityInput },
) => {
  if (input.id === 8) {
    mockError('updateAvailability');
  }

  // eslint-disable-next-line new-cap
  const availabilityOptions = mocks.Query().availabilityOptions();

  const description = availabilityOptions.find(
    (availability) => availability.id === input.id,
  )?.description;

  return attachToViewerWithId({
    availability: { id: input.id, description },
  });
};

const updatePreferredWorkTypes = (
  _: unknown,
  { input }: { input: Schema.UpdatePreferredWorkTypesInput },
) => {
  if (input.ids.includes(4)) {
    mockError('updatePreferredWorkTypes');
  }
  const workTypeValues = input.ids.map((id) => ({
    id,
  }));

  return attachToViewerWithId({ workTypes: workTypeValues });
};

const updatePreferredLocations2 = (
  _: unknown,
  {
    input: { preferredLocations: inputPreferredLocations },
  }: { input: { preferredLocations: Schema.PreferredLocation2Input[] } },
) => {
  const preferredLocations = preferredLocations2Data.filter(
    (location) => !inputPreferredLocations.includes({ id: location.id }),
  );

  return attachToViewerWithId({
    preferredLocations2: [
      ...preferredLocations,
      {
        id: 1234,
        description: 'Cremorne VIC 3121',
        shortDescription: 'Cremorne, Victoria',
        countryCode: 'AU',
      },
    ],
    salaryPreferences2: salaryPreferences,
    rightsToWork,
  });
};

const updateApproachability = (
  _: unknown,
  { input }: { input: Schema.UpdateApproachabilityInput },
) => {
  if (input.approachable === false) {
    mockError('updateApproachability');
  }

  return attachToViewerWithId({ approachability: input });
};

const updatePreferredClassification = (
  _: unknown,
  { input }: { input: Schema.UpdateClassificationInput },
) => attachToViewerWithId({ preferredClassification: input.classification });

let persistedResumes = mockOverrides.resumes({ skipQueryError: true });

const setDefaultResume = (
  _: unknown,
  { input }: { input: Schema.SetDefaultResumeInput },
) => {
  if (input.id === errorResumeId) {
    mockError('setDefaultResume');
  }
  const resumes = persistedResumes.map((resume) => {
    const isDefault = resume.id === input.id;
    return { ...resume, isDefault };
  });
  persistedResumes = resumes;
  return {
    ...MutationResult,
    ...attachToViewerWithId({
      resumes,
    }),
  };
};

const deleteResume = (
  _: unknown,
  { input }: { input: Schema.DeleteResumeInput },
) => {
  if (input.id === errorResumeId) {
    mockError('deleteResume');
  }
  const resumes = persistedResumes.filter((resume) => resume.id !== input.id);
  persistedResumes = resumes;
  return {
    ...MutationResult,
    ...attachToViewerWithId({
      resumes,
    }),
  };
};

const processUploadedResume = (
  _: unknown,
  { input }: { input: Schema.ProcessUploadedResumeInput },
) => {
  if (input.id) {
    const newUploadedResume = {
      ...uploadedOKResume,
      id: input.id,
      isDefault: input.isDefault === false,
    };
    persistedResumes.push(newUploadedResume as Schema.Resume);
    return {
      resume: {
        id: input.id,
      },
      ...attachToViewerWithId({
        resumes: persistedResumes,
      }),
    };
  }

  return input;
};

const generateResumeFromProfile = () => {
  handleQueryError('generateResume');
  // eslint-disable-next-line new-cap
  const resume = mocks.Resume();
  persistedResumes.push(resume as Schema.Resume);
  return {
    resume,
    ...attachToViewerWithId({
      resumes: persistedResumes,
    }),
  };
};

const roles = (
  {
    confirmedRoles: confirmedRolesInput,
    unconfirmedRoles: unconfirmedRolesInput,
  }: {
    confirmedRoles?: Schema.Role[];
    unconfirmedRoles?: Schema.Role[];
  },
  { status }: Schema.CandidateRolesArgs,
) => {
  handleQueryError('roles');

  if (confirmedRolesInput || unconfirmedRolesInput) {
    if (status === 'unconfirmed' && unconfirmedRolesInput) {
      return unconfirmedRolesInput;
    }
    return confirmedRolesInput;
  }

  if (returnEmptyState('roles')) {
    return [];
  }

  if (status === 'unconfirmed') {
    if (returnState('unconfirmedRoles', 'error')) {
      throw new Error('Unconfirmed roles load error');
    }
    return unconfirmedRoles;
  }

  return confirmedRoles;
};

const profileAvatarStatus = (_parent: unknown, args: { avatarId?: string }) => {
  handleQueryError('profileAvatarStatus'); // `testData=profileAvatarStatus:query:error`

  // Extract avatarId from args
  const avatarId = args?.avatarId;

  // If no avatarId provided, return pending as default
  if (!avatarId) {
    return {
      __typename: 'ProfileAvatarStatusPending' as const,
    };
  }

  // Check for test states
  if (returnState('profileAvatarStatus', 'timeout')) {
    // Return pending status that will never transition (simulating timeout)
    const status = {
      __typename: 'ProfileAvatarStatusPending' as const,
    };
    avatarStatusStore.set(avatarId, status);
    return status;
  }

  if (returnState('profileAvatarStatus', 'moderation')) {
    return {
      __typename: 'ProfileAvatarStatusFailure' as const,
      error: {
        __typename: 'ProfileAvatarStatusModerationFailedError',
        title: `Photo doesn't meet guidelines`,
        message: `This photo contains content that doesn't meet our community guidelines. Please choose a different photo.`,
      },
    };
  }

  if (returnState('profileAvatarStatus', 'virus')) {
    return {
      __typename: 'ProfileAvatarStatusFailure' as const,
      error: {
        __typename: 'ProfileAvatarStatusHasVirusError',
        title: 'File not accepted',
        message: `We detected a virus with this file and couldn't upload it. Please try a different photo.`,
      },
    };
  }

  if (returnState('profileAvatarStatus', 'generic-failure')) {
    return {
      __typename: 'ProfileAvatarStatusFailure' as const,
      error: {
        __typename: 'ProfileAvatarStatusGenericError',
        title: 'Something went wrong',
        message: 'Please try again or check back later.',
      },
    };
  }

  // Default: return success after first poll
  // First poll returns pending, second poll returns success (simulates processing time)
  const storedStatus = avatarStatusStore.get(avatarId);
  if (storedStatus) {
    // If already stored (from first poll), return success and update store
    const successStatus = {
      __typename: 'ProfileAvatarStatusSuccess' as const,
    };
    avatarStatusStore.set(avatarId, successStatus);
    return successStatus;
  }

  // First poll: return pending
  const status = {
    __typename: 'ProfileAvatarStatusPending' as const,
  };
  avatarStatusStore.set(avatarId, status);
  return status;
};

const qualifications = (
  {
    confirmedQualifications: confirmedQualificationsInput,
    unconfirmedQualifications: unconfirmedQualificationsInput,
  }: {
    confirmedQualifications: Education[];
    unconfirmedQualifications: Education[];
  },
  { status }: Schema.CandidateQualificationsArgs,
) => {
  handleQueryError('qualifications');

  if (confirmedQualificationsInput || unconfirmedQualificationsInput) {
    if (status === 'unconfirmed' && unconfirmedQualificationsInput) {
      return unconfirmedQualificationsInput.map((unconfirmedQualification) => ({
        ...unconfirmedQualification,
        formattedCompletion: 'Finished 2020',
      }));
    }

    return confirmedQualificationsInput.map((confirmedQualification) => ({
      ...confirmedQualification,
      formattedCompletion: 'Finished 2020',
    }));
  }

  if (returnEmptyState('qualifications')) {
    return [];
  }

  if (status === 'unconfirmed') {
    if (returnState('unconfirmedQualifications', 'error')) {
      throw new Error('Unconfirmed qualifications load error');
    }
    return unconfirmedQualifications;
  }

  return confirmedQualifications;
};

const UpdateCurrentLocation2Payload = {
  __resolveType: () => 'UpdateCurrentLocation2Success',
};

const UpdatePreferredLocations2Payload = {
  __resolveType: () => 'UpdatePreferredLocations2Success',
};

const UpdateProfileVisibility2Payload = {
  __resolveType: () => 'UpdateProfileVisibility2Success',
};

const ProfileSuggestLocationType = {
  __resolveType: () => 'ProfileSuggestLocation',
};

const ProfileSuggestLocation = {
  id: () => 1234,
  contextualName: () => 'Cremorne VIC 3121',
  shortName: () => 'Cremorne, Victoria',
  kind: () => 'Suburb',
  countryCode: () => 'AU',
  highlights: () => [
    {
      start: 2,
      end: 10,
    },
  ],
};

const ReferenceCheck = {
  id: () => 'f13f5ae1-1f42-4f86-8953-8153a78baaba',
  references: () => [
    {
      candidateRoleTitle: () => 'Engineer',
      refereeFullName: () => 'John Doe',
      relationshipText: () => 'Your manager at SEEK',
      organisationName: () => 'SEEK',
      refereeRoleTitle: () => 'Senior Manager',
      fromDate: {
        shortAbsoluteLabel: () => '1 Jan 2020',
      },
      toDate: {
        shortAbsoluteLabel: () => '1 Jan 2024',
      },
      referenceDate: {
        shortAbsoluteLabel: () => '18 May 2023',
      },
    },
    {
      candidateRoleTitle: () => 'Senior Engineer',
      refereeFullName: () => 'Jane Smith',
      relationshipText: () => 'Your colleague at Another Corp',
      organisationName: () => 'Another Corp',
      refereeRoleTitle: () => 'Team Lead',
      fromDate: {
        shortAbsoluteLabel: () => '1 Jan 2020',
      },
      toDate: null,
      referenceDate: {
        shortAbsoluteLabel: () => '18 May 2023',
      },
    },
  ],
};

const ProfileInsightsActivity = {
  __resolveType: (obj: any) => obj.__typename,
};

const ProfileInsights = {
  activities: () => profileInsightsWithActivities.activities,
};

const ProfileVerifications = {
  verified: () => verificationsComplete?.verified,
  verifiable: () => verificationsComplete?.verifiable,
  nudge: () => verificationsDigitalIdNudge.nudge,
};

const VerificationsNudge2 = {
  __resolveType: (obj: any) => obj.__typename,
};

const BaseVerifiableCredential = {
  __resolveType: (obj: any) => {
    if ('__typename' in obj) {
      return obj.__typename;
    }
    return 'IdentityVerifiableCredential';
  },
};

const Candidate = { roles, qualifications, profileAvatarStatus };

const Mutation = {
  confirmQualification,
  confirmRole,
  createProfileAvatar,
  deleteLanguageProficiency,
  deleteLicence,
  deleteProfileAvatar,
  deleteQualification,
  deleteResume,
  deleteRole,
  generateResumeFromProfile,
  processUploadedResume,
  rejectUnconfirmedData,
  setDefaultResume,
  updateApproachability,
  updateCareerObjectives,
  updateCurrentLocation2,
  updateLanguageProficiency,
  updateLicence,
  updatePreferredLocations2,
  updateAvailability,
  updatePersonalDetails,
  updatePreferredClassification,
  updateProfileVisibility,
  updateProfileVisibility2,
  updateQualification,
  updateRole,
  updateSkills,
  updatePreferredWorkTypes,
  updateVerifiedQualification,
};

const CompletionDate = {
  __resolveType: (obj: Schema.CompletionDate) => {
    if ('month' in obj) {
      return 'MonthYear';
    }

    return 'Year';
  },
};

const CreateProfileAvatarPayload = {
  __resolveType: (obj: { __typename?: string }) => {
    if (obj.__typename === 'CreateProfileAvatarFailure') {
      return 'CreateProfileAvatarFailure';
    }
    if (obj.__typename === 'CreateProfileAvatarSuccess') {
      return 'CreateProfileAvatarSuccess';
    }
    return 'CreateProfileAvatarSuccess';
  },
};

const CreateProfileAvatarError = {
  __resolveType: (obj: any) => {
    // Check if the object has __typename to help with type resolution
    if (obj && typeof obj === 'object' && '__typename' in obj) {
      return obj.__typename;
    }
    // The mock system auto-generates a concrete type with the interface name
    // Return the concrete type name that matches the interface
    return 'CreateProfileAvatarError';
  },
};

export const resolverOverrides = (mockStore: IMockStore) => ({
  ...mockResolvers(mockStore),
  Mutation,
  Candidate,
  CompletionDate,
  CreateProfileAvatarPayload,
  CreateProfileAvatarError,
  UpdateCurrentLocation2Payload,
  UpdatePreferredLocations2Payload,
  UpdateProfileVisibility2Payload,
  ProfileSuggestLocationType,
  ProfileSuggestLocation,
  ProfileInsights,
  ProfileInsightsActivity,
  ProfileVerifications,
  VerificationsNudge2,
  BaseVerifiableCredential,
  ReferenceCheck,
});
